/**
 * Chat error UI: boundary (catches render errors), fallback (recovery UI), and
 * banner (API/stream errors). All reusable across chat UIs.
 */

import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { Component, type ErrorInfo, type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// =============================================================================
// ChatErrorFallback
// =============================================================================

/** Fallback UI when the chat message area throws during render. Used by {@link ChatErrorBoundary}. */
interface ChatErrorFallbackProps {
  error: Error;
  onRetry: () => void;
}

const ChatErrorFallback = ({ error, onRetry }: ChatErrorFallbackProps) => (
  <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
    <div className="rounded-full bg-red-50 p-3">
      <WarningCircleIcon className="size-6 text-red-600" />
    </div>
    <div className="space-y-1">
      <h2 className="text-sm font-semibold text-gray-900">
        Something went wrong
      </h2>
      <p className="max-w-sm text-xs text-gray-500">
        {error.message ||
          "An unexpected error occurred while rendering this section."}
      </p>
    </div>
    <Button variant="outline" size="sm" onPress={onRetry}>
      <ArrowClockwiseIcon className="mr-2 size-3.5" />
      Try again
    </Button>
  </div>
);

// =============================================================================
// ChatErrorBoundary
// =============================================================================

/**
 * Error boundary for the chat message area. Catches render-phase errors and
 * shows a recovery UI. Must be a class component (React only supports error
 * boundaries on classes).
 */
export interface ChatErrorBoundaryProps {
  children: ReactNode;
}

interface ChatErrorBoundaryState {
  error: Error | null;
}

class ChatErrorBoundary extends Component<
  ChatErrorBoundaryProps,
  ChatErrorBoundaryState
> {
  constructor(props: ChatErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ChatErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ChatErrorBoundary] Uncaught render error:",
      error,
      info.componentStack,
    );
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      return (
        <ChatErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// ChatErrorBanner
// =============================================================================

/**
 * Map raw API/stream error strings to user-friendly messages.
 * Returns a friendly summary and whether the error is worth retrying.
 */
function classifyError(raw: string): {
  friendly: string;
  retriable: boolean;
} {
  if (raw.includes("Invalid 'signature' in 'thinking'")) {
    return {
      friendly:
        "This conversation encountered a temporary issue. Please try again.",
      retriable: true,
    };
  }
  if (
    // Raw payload-size rejection. Backend 413 uses the new phrasing;
    // infrastructure layers (proxy/CDN) still surface the legacy strings.
    raw.includes("too large to send") ||
    raw.includes("Request body too large") ||
    raw.includes("bytes exceeds")
  ) {
    return {
      friendly:
        "Your message is too large to send. Try removing any attachments and sending again.",
      retriable: false,
    };
  }
  if (
    // Conversation-length cap from the message-count guard (backend 422).
    // New wording ("reached the maximum length") and the legacy "Message
    // count..." both land here.
    raw.includes("reached the maximum length") ||
    raw.includes("Message count")
  ) {
    return {
      friendly:
        "This conversation has reached the maximum length. To keep chatting with Herald, start a new conversation.",
      retriable: false,
    };
  }
  if (raw.includes("Authentication") || raw.includes("401")) {
    return {
      friendly: "Your session has expired. Please refresh the page to log in.",
      retriable: false,
    };
  }
  if (/network|ERR_NETWORK|Failed to fetch/i.test(raw)) {
    return {
      friendly: "Network connection lost. Check your connection and retry.",
      retriable: true,
    };
  }
  return {
    friendly: "Something went wrong. Please try again.",
    retriable: true,
  };
}

/** Banner shown when the chat API or stream returns an error. Place above the chat input. */
export interface ChatErrorBannerProps {
  message: string | null | undefined;
  /** Short heading next to the icon. @default "Something went wrong" */
  title?: string;
  /**
   * Retry handler — in Herald chat, wire to `useChat` `clearError` then `regenerate`
   * so the last turn is requested again.
   */
  onRetry?: () => void;
  /** When true, disables Try again (e.g. while a stream is active). */
  isRetryDisabled?: boolean;
}

export const ChatErrorBanner = ({
  message,
  title = "Something went wrong",
  onRetry,
  isRetryDisabled = false,
}: ChatErrorBannerProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!message) return null;

  const { friendly, retriable } = classifyError(message);
  const showRetry = onRetry != null && retriable;
  const hasRawDetails = friendly !== message;

  return (
    <div
      role="alert"
      className={cn(
        "mx-4 mb-2 flex min-w-0 items-start gap-x-3 rounded-lg border border-border bg-background px-4 py-3 sm:mx-6",
        "text-left text-xs leading-snug text-red-600",
      )}
    >
      <span
        aria-hidden
        className="inline-flex size-[1lh] shrink-0 items-center justify-center self-start text-red-600"
      >
        <WarningCircleIcon className="size-full" />
      </span>
      <div className="flex min-w-0 flex-1 basis-0 flex-col gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="m-0 min-w-0 font-medium text-red-600">{title}</p>
          <p className="m-0 min-w-0 text-red-600/90">{friendly}</p>
          {hasRawDetails && (
            <button
              type="button"
              onClick={() => setDetailsOpen((prev) => !prev)}
              className="mt-1 flex items-center gap-1 text-[11px] text-red-400 hover:text-red-600"
            >
              <CaretDownIcon
                className={cn(
                  "size-3 transition-transform",
                  detailsOpen && "rotate-180",
                )}
              />
              {detailsOpen ? "Hide details" : "Show details"}
            </button>
          )}
          {hasRawDetails && detailsOpen && (
            <p className="m-0 mt-1 min-w-0 wrap-break-word whitespace-normal break-all rounded bg-red-50 px-2 py-1.5 font-mono text-[11px] text-red-600/80">
              {message}
            </p>
          )}
        </div>
        {showRetry ? (
          <Button
            variant="ghost"
            size="sm"
            onPress={onRetry}
            isDisabled={isRetryDisabled}
            className="h-7 w-fit self-start"
          >
            <ArrowClockwiseIcon className="mr-1.5 size-3.5" />
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
};

// =============================================================================
// Exports
// =============================================================================

export default ChatErrorBoundary;
